import { pruneSchedulesForTable } from "./pruneSchedules";
import type { Schedules, TableConfig } from "./types";

/** One Tables-section edit, applied as a single write. `schedules` is present only when entries moved. */
export interface TablesChange {
  tables: TableConfig[];
  schedules?: Schedules;
}

/** Row index → the name its schedule entries wait under while the row's own name is blank or taken. */
export type ParkedNames = ReadonlyMap<number, string>;

interface Plan {
  change: TablesChange;
  parked: Map<number, string>;
}

// Case-insensitive, like the runner's applySchedule().
const key = (name: unknown) => (typeof name === "string" ? name.trim().toLowerCase() : "");

function withSchedules(tables: TableConfig[], schedules: Schedules | null): TablesChange {
  return schedules ? { tables, schedules } : { tables };
}

/** Point every entry filed under `from` at `to` instead, or null if none was. */
export function renameSchedulesForTable(
  schedules: Schedules,
  from: string,
  to: string,
): Schedules | null {
  const target = key(from);
  if (!target) return null;
  let changed = false;
  const next: Schedules = {};
  for (const [cadence, entries] of Object.entries(schedules)) {
    next[cadence] = (entries ?? []).map((e) => {
      if (key(e.name) !== target) return e;
      changed = true;
      return { ...e, name: to };
    });
  }
  return changed ? next : null;
}

function hasEntryNamed(schedules: Schedules, name: string): boolean {
  return Object.values(schedules).some((entries) =>
    (entries ?? []).some((e) => key(e.name) === key(name)),
  );
}

/** The name row `index`'s schedule entries are filed under right now. */
export function filedName(
  tables: TableConfig[],
  schedules: Schedules,
  parked: ParkedNames,
  index: number,
): string {
  const p = parked.get(index);
  // A parked name goes stale once no entry carries it or a table claims it (a revert, a reload).
  const live =
    p !== undefined && hasEntryNamed(schedules, p) && !tables.some((t) => key(t.name) === key(p));
  return live ? p : (tables[index]?.name ?? "");
}

function nameTaken(tables: TableConfig[], parked: ParkedNames, index: number, name: string) {
  const k = key(name);
  return (
    tables.some((t, i) => i !== index && key(t.name) === k) ||
    [...parked].some(([i, p]) => i !== index && key(p) === k)
  );
}

/**
 * Replace row `index` with `next`, carrying a rename through to every schedule entry (SIM-3572).
 * Runs on every keystroke, so a name that is momentarily blank or another table's parks the
 * entries under the last usable name rather than stranding them or merging them into that table's.
 */
export function updateTableAt(
  tables: TableConfig[],
  schedules: Schedules,
  parked: ParkedNames,
  index: number,
  next: TableConfig,
): Plan {
  const nextTables = tables.map((t, i) => (i === index ? next : t));
  const from = filedName(tables, schedules, parked, index);
  const nextParked = new Map(parked);
  nextParked.delete(index);
  if (from === next.name) return { change: { tables: nextTables }, parked: nextParked };
  if (!next.name.trim() || nameTaken(tables, parked, index, next.name)) {
    if (key(from)) nextParked.set(index, from);
    return { change: { tables: nextTables }, parked: nextParked };
  }
  const moved = renameSchedulesForTable(schedules, from, next.name);
  return { change: withSchedules(nextTables, moved), parked: nextParked };
}

/** Drop row `index` and every schedule entry filed under it, as one change (SIM-3573). */
export function removeTableAt(
  tables: TableConfig[],
  schedules: Schedules,
  parked: ParkedNames,
  index: number,
): Plan {
  const nextTables = tables.filter((_, i) => i !== index);
  const filed = filedName(tables, schedules, parked, index);
  // A surviving table with the same name still owns those entries.
  const shared = nextTables.some((t) => key(t.name) === key(filed));
  const pruned = shared ? null : pruneSchedulesForTable(schedules, filed);
  const nextParked = new Map<number, string>();
  for (const [i, p] of parked) {
    if (i !== index) nextParked.set(i > index ? i - 1 : i, p);
  }
  return { change: withSchedules(nextTables, pruned), parked: nextParked };
}
